<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import env from "@/env.js";
import WxScan from "@/components/WxScan.vue";
import { useFetch } from "@/api/fetch.js";
import { redirectToAdminApp } from "@/utils.js";

const route = useRoute();
const platform = computed(() => ["console", "reseller"].includes(route.query.target) ? route.query.target : "");
const resellerScan = ref(null);
const { postJson } = useFetch();

const info = ref("");
const infoShow = ref(false);
const loading = ref(true);
const existingSessionChecked = ref(false);

const homePath = `${env.host}/`;
const chainType = computed(() => String(route.query.chainType || "").trim());
const callbackPath = computed(() => {
  if (!chainType.value) return "/login/LoginCallback";
  return `/login/LoginCallback?chainType=${encodeURIComponent(chainType.value)}`;
});

const reloadPage = () => window.location.reload();

const showInfo = (message) => {
  info.value = message;
  infoShow.value = true;
};

onMounted(async () => {
  try {
    if (platform.value) {
      const session = await postJson("wxScan", "getSessionInfo", { sessionInfo: platform.value });
      if (session.info === "OK") {
        window.location.assign(`${env.host}/${platform.value}/`);
        return;
      }
      const result = await postJson("wxScan", "createScanLoginSession", { sessionInfo: platform.value });
      if (result.info !== "OK") throw new Error(result.error || "暂时无法获取二维码");
      resellerScan.value = result;
      return;
    }
    // 本地预览只用于检查登录界面与扫码流程，不继承线上浏览器会话，
    // 避免开发服务器收到有效会话后跳到同端口的 /shop。
    if (env.isLocalDevEnv) return;
    const res = await postJson("wxScan", "getSessionInfo", {
      sessionInfo: "staff",
    });
    if (res?.info === "OK") {
      redirectToAdminApp(res);
      return;
    }
  } catch (error) {
    console.error("读取登录态失败:", error);
    if (platform.value) showInfo(error.message || "暂时无法获取二维码");
  } finally {
    existingSessionChecked.value = true;
    loading.value = false;
  }
});
</script>

<template>
  <v-snackbar v-model="infoShow" :timeout="3000" color="primary" location="bottom right">
    {{ info }}
    <template #actions>
      <v-btn color="white" variant="text" @click="infoShow = false">关闭</v-btn>
    </template>
  </v-snackbar>

  <v-container class="login-page">
    <div class="top-actions">
      <v-btn size="large" variant="text" color="primary" :href="homePath" prepend-icon="mdi-arrow-left">
        返回首页
      </v-btn>
    </div>

    <v-card class="login-panel" variant="outlined">
      <v-card-title class="text-h5">微信扫码登录</v-card-title>
      <v-card-subtitle>{{ platform ? '请使用已开通平台权限的微信扫码' : '请选择有商户权限的微信账号扫码' }}</v-card-subtitle>

      <v-card-text>
        <div v-if="loading" class="loading-box">
          <v-progress-circular indeterminate color="primary" />
          <div class="text-body-2 text-grey-darken-1 mt-4">正在检查登录状态...</div>
        </div>
        <WxScan v-else-if="platform && resellerScan" :redirect-uri-path="callbackPath"
          :scan-state="resellerScan.state" :scan-appid="resellerScan.appid" :scan-redirect-uri="resellerScan.redirectUri" />
        <div v-else-if="platform" class="loading-box">
          <p>{{ info || '二维码暂不可用' }}</p>
          <v-btn class="mt-4" @click="reloadPage">重新获取</v-btn>
        </div>
        <WxScan v-else-if="existingSessionChecked" :redirect-uri-path="callbackPath" />
      </v-card-text>
    </v-card>

  </v-container>
</template>

<style scoped>
.login-page {
  min-height: 100vh;
  max-width: 560px;
  padding-top: 24px;
}

.top-actions {
  margin-bottom: 24px;
}

.login-panel {
  border-radius: 8px;
}

.loading-box {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
